import React, { useState } from "react";
import { PropertiesTable } from "./components/PropertiesTable";
import "./SceneEditor.scss";
import "./inputs/Inputs.scss";
import classnames from "classnames";
import { LoadingButton } from "./components/LoadingButton";
import { NodeContainer } from "../nodes/NodeContainer";
import { Node } from "../nodes/Node";
import * as Commons from "oc-commons/Commons";

const debouncedDirty = _.debounce(() => STAGE.dirty(), 300);

export function SceneEditor({ sceneNode, closeButton, onHeaderMouseDown }) {
  const [slide, setSlide] = useState(null);
  let [nodeStack, setNodeStack] = useState([{ node: sceneNode, parentNodeList: null }]);
  const [, forceUpdate] = useState();
  const [toasts, setToasts] = useState([]);

  const removeToast = (toast) => {
    setToasts(toasts.filter((t) => t !== toast));
  };
  const addToast = (toast) => {
    setToasts(toasts.concat(toast));
    _.delay(() => removeToast(toast), toast.expiration || 15000);
  };

  const setNodeStackAndRefreshHelper = (newNodeStack) => {
    const showHelper = (node, show) => {
      if (node && node.showHelper !== undefined) {
        node.showHelper = show;
      }
    };
    const lastNode = _.last(nodeStack).node;
    const nextNode = (_.last(newNodeStack) as any).node;
    showHelper(lastNode, false);
    showHelper(nextNode, true);
    setNodeStack(newNodeStack);
    nodeStack = newNodeStack;
    APP.sceneNode.callDebouncedUpdateSceneOn(lastNode, nextNode);
  };

  const popSelected = async () => {
    if (nodeStack.length > 1) {
      setSlide("pop-init");
      await Commons.sleep(1);
      setSlide("pop");
      await Commons.sleep(200);
      setSlide(null);
      setNodeStackAndRefreshHelper(nodeStack.slice(0, -1));
    }
  };

  const setSelected = async (node: Node, parentNodeList: NodeContainer<Node>) => {
    setNodeStackAndRefreshHelper(nodeStack.concat([{ node, parentNodeList }]));
    setSlide("push-init");
    await Commons.sleep(1);
    setSlide("push");
    await Commons.sleep(200);
    setSlide(null);
  };

  sceneNode.forceUiUpdate = forceUpdate;

  return (
    <div className="scene-editor-group" onClick={debouncedDirty}>
      {nodeStack.map(({ node, parentNodeList }, index) => {
        const slideIndex = index - nodeStack.length + 2;
        const visible = slideIndex >= 0;
        const NodeRender = node?.render;
        const isTopNode = index == nodeStack.length - 1;
        return (
          <div
            key={index}
            className={classnames("scene-editor", slide, "slide-" + slideIndex)}
            style={visible ? {} : { display: "none" }}
          >
            <div className="title-row flex-row" onMouseDown={onHeaderMouseDown}>
              {index > 0 && (
                <div
                  key="back"
                  className="back-button button"
                  onClick={() => popSelected()}
                  title="zurück"
                >
                  ◀
                </div>
              )}
              <div key="title" className="title">
                {node.getName()}
              </div>
              {closeButton()}
            </div>
            <div className="scene-editor-body">
              {NodeRender ? (
                <NodeRender
                  node={node}
                  setSelected={setSelected}
                  nodeIndex={nodeStack.length}
                  isTopNode={isTopNode}
                />
              ) : (
                <PropertiesTable
                  node={node}
                  setSelected={setSelected}
                  nodeIndex={nodeStack.length}
                  isTopNode={isTopNode}
                />
              )}
            </div>
            <div className="flex-row bottom-row">
              <React.Fragment>
                <LoadingButton
                  className="save button"
                  onClickPromise={async () => {
                    await node.saveToServer();
                  }}
                  title="speichert den Datensatz"
                >
                  speichern
                </LoadingButton>
                {parentNodeList && parentNodeList.canModifyList && (
                  <LoadingButton
                    key="clone"
                    className="clone button"
                    onClickPromise={async () => {
                      const clone = parentNodeList.createNode();
                      const json = node.toJSON();
                      delete json.id;
                      clone.fromJSON(json);
                      if (node.getKeyProperty()) {
                        clone.getKeyProperty().set(node.getKeyProperty().get() + "_Kopie");
                      }
                      parentNodeList.add(clone);
                      popSelected();
                      await Commons.sleep(500);
                      setSelected(clone, parentNodeList);
                    }}
                    title="erstellt eine Kopie"
                  >
                    kopieren
                  </LoadingButton>
                )}
                {parentNodeList && parentNodeList.canModifyList && (
                  <LoadingButton
                    key="delete"
                    className="delete button"
                    onClickPromise={async () => {
                      await node.removeOnServer();
                      parentNodeList.remove(node);
                      popSelected();
                      addToast({
                        message: "Der Eintrag wurde gelöscht.",
                        button: {
                          text: "Wiederherstellen",
                          action: async () => {
                            node.id = null;
                            await node.saveToServer();
                            parentNodeList.add(node);
                            setSelected(node, parentNodeList);
                          },
                        },
                      });
                    }}
                    title="löschen"
                  >
                    löschen
                  </LoadingButton>
                )}
              </React.Fragment>
            </div>
            {toasts.length > 0 && (
              <div className="flex-column bottom-message-row">
                {toasts.map((t, i) => {
                  return (
                    <div key={i} className="bottom-toast flex-row">
                      {t.message}
                      {t.button && (
                        <LoadingButton
                          className="toast-action"
                          onClickPromise={async () => t.button.action().then(() => removeToast(t))}
                        >
                          {t.button.text}
                        </LoadingButton>
                      )}
                      <div
                        className="close toast-action"
                        onClick={() => removeToast(t)}
                        title="ausblenden"
                      >
                        ✖
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
